import React, { useMemo, useState } from "react";

import { faChevronLeft, faLock } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { List, ListItemText, TextField } from "@material-ui/core";
import sortBy from "lodash/sortBy";
import { useDispatch, useSelector } from "react-redux";

import { space } from "constants/theme";
import { PickerListItem } from "components/PickerListItem";
import {
  DialogActions,
  DialogContent,
  DialogTitle,
  ZoomyDialog,
} from "components/MaterialDialog";
import { SecondaryButton } from "components/Form";
import { SaleType, UnselectableSaleTypes } from "constants/sale";
import { Species } from "constants/species";
import { openSaleModal } from "lib/navigation";
import {
  getAvailableSaleyards,
  getSaleSubTypes,
  getSaleTypes,
  getSpecies,
  selectSpeciesBySaleyardIdLookup,
} from "selectors";
import { toTitleCase } from "lib";
import { openDispatchingModal } from "actions";
import { ModalTypes } from "constants/navigation";
import { SmallFaIcon } from "components/FaIcon";

const getTitle = (
  phase,
  saleTypePhase,
  selectedSaleType,
  selectedSpeciesName,
) => {
  if (phase === saleTypePhase.SALE_TYPE) {
    return "Select Sale Type";
  } else if (phase === saleTypePhase.SPECIES) {
    return `${selectedSaleType} > Select Species`;
  } else {
    return `${selectedSaleType} > ${selectedSpeciesName !== selectedSaleType ? `${toTitleCase(selectedSpeciesName)} >` : ""} Select Sub Sale Type`;
  }
};

// Cattle then Sheep are shown first in the species list, remaining follow alphabetical order.
const PRIORITY_SPECIES_IDS = [Species.CATTLE, Species.SHEEP];

// For Paddock sales, "Private Sale" is shown first in the sub-sale type list if available.
const PADDOCK_PRIORITY_SUB_TYPE_NAMES = ["Private Sale"];

// Paddock and Over Hooks are shown first, remaining types follow enum order.
const PRIORITY_SALE_TYPES = [SaleType.PADDOCK, SaleType.OVER_HOOKS];

const ORDERED_SALE_TYPES = [
  ...PRIORITY_SALE_TYPES,
  ...Object.values(SaleType).filter(st => !PRIORITY_SALE_TYPES.includes(st)),
];

// Sale types whose species is fixed by the backend — species selection is skipped for these.
// Mirrors the logic in SaleModal's handleReInit and onSubmit.
const FIXED_SPECIES_BY_SALE_TYPE: Partial<Record<SaleType, number>> = {
  [SaleType.CLEARING]: Species.CLEARING_SALE,
  [SaleType.SUNDRY_SALE]: Species.SUNDRY_SALE,
};

const saleTypePhase = {
  SALE_TYPE: "SALE_TYPE",
  SPECIES: "SPECIES",
  SALE_SUB_TYPE: "SALE_SUB_TYPE",
};

type Phase = (typeof saleTypePhase)[keyof typeof saleTypePhase];

interface SaleTypePickerModalProps {
  onClose: () => void;
}

export function SaleTypePickerModal({
  onClose,
}: SaleTypePickerModalProps): React.JSX.Element {
  const dispatch = useDispatch();

  const handleOpenSaleModal = (saleId, returnTo, initialValues, saleType) => {
    // for hooks and paddock enter into mobile create modal
    if (saleType === SaleType.OVER_HOOKS || saleType === SaleType.PADDOCK) {
      dispatch(
        openDispatchingModal(
          ModalTypes.MobileTransactionalCreateSaleModal,
          null,
          {
            isOpen: true,
            saleType,
            initialValues,
          },
        ),
      );
    } else {
      openSaleModal(saleId, returnTo, initialValues);
    }
  };

  const [phase, setPhase] = useState<Phase>(saleTypePhase.SALE_TYPE);
  const [selectedSaleType, setSelectedSaleType] = useState<string | null>(null);
  const [selectedSpeciesId, setSelectedSpeciesId] = useState<number | null>(
    null,
  );
  const [search, setSearch] = useState("");

  const availableSaleTypes: string[] = useSelector(getSaleTypes);
  const saleSubTypesById: Record<string, SaleSubType> =
    useSelector(getSaleSubTypes);
  const speciesByIdLookup: Record<number, Species> = useSelector(getSpecies);
  const availableSaleyards = useSelector(getAvailableSaleyards);

  const saleyardId = availableSaleyards[0].saleyard_id;

  const speciesBySaleyard = useSelector(state =>
    (
      selectSpeciesBySaleyardIdLookup as (
        state: RootState,
        saleyardId: number,
      ) => Species[]
    )(state, saleyardId),
  );

  // Phase 1: sale type options
  const allSaleTypeOptions = ORDERED_SALE_TYPES.filter(
    saleType => !UnselectableSaleTypes.includes(saleType),
  ).map(saleType => ({
    value: saleType,
    isDisabled: !availableSaleTypes.includes(saleType),
  }));

  const filteredSaleTypeOptions = allSaleTypeOptions.filter(({ value }) =>
    value.toLowerCase().includes(search.toLowerCase()),
  );

  // Phase 2: species for the selected sale type's saleyard — Cattle first, Sheep second, rest alphabetically
  const sortedSpecies = useMemo(() => {
    const prioritizedSpecies = PRIORITY_SPECIES_IDS.map(id =>
      speciesBySaleyard.find(s => s.id === id),
    ).filter(Boolean);

    const rest = sortBy(
      speciesBySaleyard.filter(s => !PRIORITY_SPECIES_IDS.includes(s.id)),
      "name",
    );
    return [...prioritizedSpecies, ...rest];
  }, [speciesBySaleyard]);

  const filteredSpeciesOptions = sortedSpecies.filter(species =>
    species.name.toLowerCase().includes(search.toLowerCase()),
  );

  // Phase 3: sub-sale type options filtered by selected sale type and species
  const subTypesForSelection = useMemo(() => {
    if (!selectedSaleType || !selectedSpeciesId) {
      return [];
    }
    const filteredSaleSubTypes = Object.values(saleSubTypesById).filter(
      (st: SaleSubType) =>
        st.saleType === selectedSaleType && st.speciesId === selectedSpeciesId,
    );
    const saleTypeLower = selectedSaleType.toLowerCase();
    const saleSubTypeNameMatchesSaleType = filteredSaleSubTypes.filter(
      st => st.name.toLowerCase() === saleTypeLower,
    );
    const remainingSaleSubTypes = filteredSaleSubTypes.filter(
      st => st.name.toLowerCase() !== saleTypeLower,
    );

    // For Paddock sales, surface "Private Sale" above other sub-types.
    if (selectedSaleType === SaleType.PADDOCK) {
      const priorityNamesLower = PADDOCK_PRIORITY_SUB_TYPE_NAMES.map(n =>
        n.toLowerCase(),
      );

      const prioritizedSaleSubTypes = priorityNamesLower
        .map(name =>
          remainingSaleSubTypes.find(st => st.name.toLowerCase() === name),
        )
        .filter(Boolean);

      const rest = sortBy(
        remainingSaleSubTypes.filter(
          st => !priorityNamesLower.includes(st.name.toLowerCase()),
        ),
        "name",
      );
      return [
        ...saleSubTypeNameMatchesSaleType,
        ...prioritizedSaleSubTypes,
        ...rest,
      ];
    }
    return [
      ...saleSubTypeNameMatchesSaleType,
      ...sortBy(remainingSaleSubTypes, "name"),
    ];
  }, [selectedSaleType, selectedSpeciesId, saleSubTypesById]);

  const filteredSubTypeOptions = subTypesForSelection.filter(st =>
    st.name.toLowerCase().includes(search.toLowerCase()),
  );

  const handleSelectSaleType = (saleType: string) => {
    const fixedSpeciesId = FIXED_SPECIES_BY_SALE_TYPE[saleType as SaleType];

    if (fixedSpeciesId !== undefined) {
      // Species is determined by the sale type — skip phase 2.
      const hasSubTypes = Object.values(saleSubTypesById).some(
        (st: SaleSubType) =>
          st.saleType === saleType && st.speciesId === fixedSpeciesId,
      );
      if (hasSubTypes) {
        setSelectedSaleType(saleType);
        setSelectedSpeciesId(fixedSpeciesId);
        setSearch("");
        setPhase(saleTypePhase.SALE_SUB_TYPE);
      } else {
        onClose();
        handleOpenSaleModal(
          null,
          "",
          {
            sale_type: saleType,
            species_id: fixedSpeciesId,
          },
          saleType,
        );
      }
    } else {
      setSelectedSaleType(saleType);
      setSearch("");
      setPhase(saleTypePhase.SPECIES);
    }
  };

  const handleSelectSpecies = (speciesId: number) => {
    const hasSubTypes = Object.values(saleSubTypesById).some(
      (st: SaleSubType) =>
        st.saleType === selectedSaleType && st.speciesId === speciesId,
    );
    if (!hasSubTypes) {
      // No sub-types for this combination — open the sale modal directly
      onClose();
      handleOpenSaleModal(
        null,
        "",
        {
          sale_type: selectedSaleType,
          species_id: speciesId,
        },
        selectedSaleType,
      );
    } else {
      setSelectedSpeciesId(speciesId);
      setSearch("");
      setPhase(saleTypePhase.SALE_SUB_TYPE);
    }
  };

  const handleSelectSubType = (subTypeId: number) => {
    onClose();
    handleOpenSaleModal(
      null,
      "",
      {
        sale_type: selectedSaleType,
        species_id: selectedSpeciesId,
        sale_sub_type_id: subTypeId,
      },
      selectedSaleType,
    );
  };

  const handleBack = () => {
    setSearch("");
    if (phase === saleTypePhase.SALE_SUB_TYPE) {
      setSelectedSpeciesId(null);
      // If species was fixed by the sale type, go back to phase 1 (phase 2 was skipped)
      const wasFixedSpecies =
        selectedSaleType !== null &&
        FIXED_SPECIES_BY_SALE_TYPE[selectedSaleType as SaleType] !== undefined;
      if (wasFixedSpecies) {
        setSelectedSaleType(null);
        setPhase(saleTypePhase.SALE_TYPE);
      } else {
        setPhase(saleTypePhase.SPECIES);
      }
    } else if (phase === saleTypePhase.SPECIES) {
      setSelectedSaleType(null);
      setPhase(saleTypePhase.SALE_TYPE);
    }
  };

  const selectedSpeciesName = speciesByIdLookup[selectedSpeciesId]?.name;

  const title = getTitle(
    phase,
    saleTypePhase,
    selectedSaleType,
    selectedSpeciesName,
  );

  return (
    <ZoomyDialog open onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle onClose={onClose}>{title}</DialogTitle>
      <DialogContent>
        <TextField
          placeholder="Tap to search"
          value={search}
          onChange={e => setSearch(e.target.value)}
          variant="outlined"
          fullWidth
          style={{ marginBottom: space[2] }}
          inputProps={{ "aria-label": "search" }}
        />
        <List data-tour="sale-type-picker" disablePadding>
          {phase === saleTypePhase.SALE_TYPE &&
            filteredSaleTypeOptions.map(({ value, isDisabled }) => (
              <PickerListItem
                key={value}
                data-tour="sale-type-picker-item"
                button={!isDisabled as true}
                disabled={isDisabled}
                onClick={
                  isDisabled ? undefined : () => handleSelectSaleType(value)
                }
              >
                <ListItemText
                  primary={
                    isDisabled ? (
                      <>
                        <SmallFaIcon
                          color="gray44"
                          icon={faLock}
                          className="px-4"
                        />
                        {value}
                      </>
                    ) : (
                      value
                    )
                  }
                />
              </PickerListItem>
            ))}

          {phase === saleTypePhase.SPECIES &&
            filteredSpeciesOptions.map(species => (
              <PickerListItem
                key={species.id}
                data-tour="sale-type-picker-item"
                button
                onClick={() => handleSelectSpecies(species.id)}
              >
                <ListItemText primary={toTitleCase(species.name)} />
              </PickerListItem>
            ))}

          {phase === saleTypePhase.SALE_SUB_TYPE &&
            filteredSubTypeOptions.map(subType => (
              <PickerListItem
                key={subType.id}
                data-tour="sale-type-picker-item"
                button
                onClick={() => handleSelectSubType(subType.id)}
              >
                <ListItemText primary={toTitleCase(subType.name)} />
              </PickerListItem>
            ))}
        </List>
      </DialogContent>
      <DialogActions>
        {phase !== saleTypePhase.SALE_TYPE && (
          <SecondaryButton onClick={handleBack}>
            <FontAwesomeIcon icon={faChevronLeft} />
            &nbsp;Back
          </SecondaryButton>
        )}
        <SecondaryButton onClick={onClose}>Cancel</SecondaryButton>
      </DialogActions>
    </ZoomyDialog>
  );
}
